import { useCallback, useMemo, useState } from 'react';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { Divider, IconButton, ListSubheader, Menu, MenuItem } from '@mui/material';
import { RELATED_ENTITY } from 'common/constants/companies';
import { CapTableVersion } from 'common/types/company';
import { SecondaryNavigationSelector } from 'components/ui';
import { toString } from 'utilities';

interface CapTableVersionSelectorProps {
  capTableVersionsByCompany: Record<string, CapTableVersion[]>;
  onVersionSelect: (slug: string) => void;
  selectedVersion: string | number | null | undefined;
  onAddRelatedEntity?: () => void;
  onEditVersion?: () => void;
}

const CapTableVersionSelector = ({
  capTableVersionsByCompany,
  onVersionSelect,
  selectedVersion,
  onAddRelatedEntity,
  onEditVersion,
}: CapTableVersionSelectorProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const handleSelect = useCallback(
    (slug: string) => {
      handleClose();
      onVersionSelect(slug);
    },
    [handleClose, onVersionSelect]
  );

  const handleAddRelatedEntity = useCallback(() => {
    handleClose();
    onAddRelatedEntity?.();
  }, [handleClose, onAddRelatedEntity]);

  const handleEditVersion = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleClose();
      onEditVersion?.();
    },
    [handleClose, onEditVersion]
  );

  const entries = useMemo(() => Object.entries(capTableVersionsByCompany), [capTableVersionsByCompany]);
  const showCompanyHeaders = entries.length > 1;

  const selectedVersionName = useMemo(() => {
    const found = Object.values(capTableVersionsByCompany)
      .flatMap(versions => versions)
      .find(v => toString(v.id) === toString(selectedVersion));
    return found?.name ?? '';
  }, [capTableVersionsByCompany, selectedVersion]);

  if (entries.length === 0) {
    return null;
  }

  return (
    <>
      <SecondaryNavigationSelector
        data-testid="secondary-navigation__selector-cap-table-version-name"
        label="Cap Table Version"
        onClick={handleOpen}
        value={selectedVersionName}
      />
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {entries.flatMap(([companyName, versions], idx) => [
          ...(showCompanyHeaders
            ? [<ListSubheader key={`header-${companyName}`} disableSticky>{companyName}</ListSubheader>]
            : []),
          ...versions.map(version => {
            const isSelected = toString(version.id) === toString(selectedVersion);
            return (
              <MenuItem
                key={version.id}
                selected={isSelected}
                onClick={() => handleSelect(version.slug ?? '')}
                sx={isSelected && onEditVersion ? { display: 'flex', justifyContent: 'space-between', gap: 1 } : {}}>
                {version.name}
                {isSelected && onEditVersion && (
                  <IconButton size="small" onClick={handleEditVersion} aria-label="Edit cap table version">
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </MenuItem>
            );
          }),
          ...(showCompanyHeaders && idx < entries.length - 1
            ? [<Divider key={`divider-${companyName}`} />]
            : []),
        ])}
        {onAddRelatedEntity && [
          <Divider key="add-divider" />,
          <MenuItem key="add-related-entity" onClick={handleAddRelatedEntity}>
            <AddIcon fontSize="small" sx={{ mr: 1 }} />
            {RELATED_ENTITY.ADD_ACTION}
          </MenuItem>,
        ]}
      </Menu>
    </>
  );
};

export default CapTableVersionSelector;
